import { useTranslation } from '@hopin-team/provider-translation';
import axios, { AxiosError } from 'axios';
import React, { createContext, useEffect, useRef, useState } from 'react';

import { useAuthentication } from '../hooks/use-authentication';
import { useSpaceContext } from '../hooks/use-space-context';
import {
  setAuthorizationHeader,
  setBaseUrl,
  setOrgId,
  setSpaceExternalId,
} from '../services/http-client';
import { getToken } from '../services/users';

type AuthenticationState = {
  status: 'idle' | 'pending' | 'complete';
  token: string | null;
  organisationId: string;
};

const initialAuthState: AuthenticationState = {
  status: 'idle',
  token: null,
  organisationId: '',
};

export const AuthenticationContext =
  createContext<AuthenticationState>(initialAuthState);

type AuthenticationProviderProps = {
  children: React.ReactNode;
  host: string;
  organisationId: string;
};

export const AuthenticationProvider = ({
  children,
  host,
  organisationId,
}: AuthenticationProviderProps) => {
  const mounted = useRef(false);
  const { isSpaceReady, spaceExternalId, spacesEnabled } = useSpaceContext();
  const [authState, setAuthState] = useState<AuthenticationState>({
    ...initialAuthState,
    organisationId,
  });

  // Tracks the most recent space value so in-flight token requests can detect
  // whether they've been superseded by a space change.
  const latestSpaceRef = useRef(spaceExternalId);
  // Tracks the space of the previous render to detect actual changes.
  const previousSpaceRef = useRef(spaceExternalId);

  useEffect(() => {
    latestSpaceRef.current = spaceExternalId;
  }, [spaceExternalId]);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  // Reset auth immediately when the selected space changes — including while a
  // token request is in-flight — so we never apply a stale token to the new
  // space's queries.
  useEffect(() => {
    if (!spacesEnabled) return;
    if (previousSpaceRef.current === spaceExternalId) return;

    previousSpaceRef.current = spaceExternalId;
    setAuthorizationHeader(null);
    setAuthState(prev => ({ ...prev, status: 'idle', token: null }));
  }, [spacesEnabled, spaceExternalId]);

  useEffect(() => {
    // Wait until the space is resolved before requesting a token.
    if (!isSpaceReady) return;
    if (authState.status !== 'idle') return;

    const requestAuthToken = async () => {
      if (!mounted.current) {
        return;
      }

      // Capture the space at request time so we can discard the response if it
      // becomes stale before the async call completes.
      const requestedSpaceExternalId = spaceExternalId;
      setAuthState(prev => ({ ...prev, status: 'pending' }));

      try {
        const authResponse = await getToken(
          organisationId,
          requestedSpaceExternalId || undefined,
        );

        // Discard the response if the component unmounted or the space changed.
        if (
          !mounted.current ||
          latestSpaceRef.current !== requestedSpaceExternalId
        ) {
          return;
        }

        setAuthorizationHeader(authResponse.token);
        setBaseUrl(authResponse.endpoints.analytics);
        setOrgId(organisationId);
        setSpaceExternalId(requestedSpaceExternalId);

        setAuthState(prev => ({
          ...prev,
          status: 'complete',
          token: authResponse.token,
        }));
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if ((error as AxiosError).response?.status === 401) {
            window.location.assign(`${host}/sign_in`);
          }
        }
      }
    };

    requestAuthToken();
  }, [authState.status, host, isSpaceReady, organisationId, spaceExternalId]);

  return (
    <AuthenticationContext.Provider value={authState}>
      {children}
    </AuthenticationContext.Provider>
  );
};

const Loading = ({ children }: React.PropsWithChildren<unknown>) => {
  const { isLoading } = useAuthentication();
  const { t } = useTranslation();

  if (isLoading) {
    return <div className="text-white">{t('loading')}</div>;
  }

  return <>{children}</>;
};

AuthenticationProvider.Loading = Loading;
